import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { createWidget } from '@typeform/embed';
import { catchError, filter, finalize, interval, of, Subject, switchMap, take, takeUntil, takeWhile, tap, timeout } from 'rxjs';

import { TypeformService } from '../typeform.service';
import {
  TypeformFormType,
  TypeformHiddenFields,
  TypeformResponseWithPageResult,
} from '../typeform.type';

@Component({
  selector: 'app-typeform-form',
  templateUrl: './typeform-form.component.html',
  styleUrls: ['./typeform-form.component.scss'],
})
export class TypeformFormComponent implements AfterViewInit, OnDestroy {
  @Input() hiddenFields!: TypeformHiddenFields;

  @Input() type!: TypeformFormType;

  @ViewChild('form') form!: ElementRef<HTMLElement>;

  private destroy$ = new Subject<void>();

  private readonly typeformIdRecord: Record<TypeformFormType, string> = {
    [TypeformFormType.INITIAL]: 'IZ1jMCsy',
    [TypeformFormType.PONCTUAL]: 'IZ1jMCsy',
    [TypeformFormType.POST_SESSION]: 'eXWd45u8',
    [TypeformFormType.POST_LAST_SESSION]: 'Fq4IFd0G',
    [TypeformFormType.POST_TRAIN]: 'Y6OUzGGf',
    [TypeformFormType.SIX_MONTHS_POST_FIRST_TRAIN]: 'QJDfAk7l',
  };

  constructor(
    private readonly typeformService: TypeformService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  unmount!: () => void;

  ngAfterViewInit() {
    const isLastSession = this.route.snapshot.queryParams.isLastSession === '1';

    if (this.type) {
      const widgetOptions: {
        container: HTMLElement;
        onQuestionChanged: () => void;
        onEndingButtonClick: () => void;
        onSubmit: () => void;
        displayAsFullScreenModal: boolean;
        fullScreen: boolean;
        hidden: TypeformHiddenFields;
        source?: string;
      } = {
        container: this.form.nativeElement,
        onQuestionChanged: () => {
          this.typeformService.needConfirmToQuit = true;
        },
        onEndingButtonClick: () => {
          this.typeformService.needConfirmToQuit = false;
        },
        onSubmit: () => {
          this.typeformService.needConfirmToQuit = false;
          if (this.typeformService.talker) {
            if (this.type !== TypeformFormType.POST_SESSION) {
              // For non-POST_SESSION forms
              let attempts = 0;
              const maxAttempts = 5;
              
              interval(3000)
              .pipe(
                tap(() => attempts++),
                takeWhile(() => attempts <= maxAttempts),
                switchMap(() => this.typeformService.retrieveResponse(
                    this.type, 
                    this.hiddenFields.id_parcours
                  ).pipe(
                    catchError(error => {
                      console.error('Error retrieving response:', error);
                      return of(null);
                    })
                  )),
                filter((response): response is TypeformResponseWithPageResult => 
                  response !== null
                ),
                take(1),
                timeout(20000),
                takeUntil(this.destroy$),
                catchError(error => of(null)),
                finalize(() => {
                  this.redirectToResults();
                })
              )
              .subscribe();
            } else {
              // For POST_SESSION forms
              setTimeout(() => {
                this.redirectToResults();
              }, 6000);
            }
            // Fix it
            // this.typeformService.saveResponse({
            //   responseId,
            //   type: this.type,
            //   talkerId: this.hiddenFields.id_user
            // }).pipe(
            //   filter(() => !!this.profileService.profile),
            //   delay(3000))
            //   .subscribe(() => {
            //     if (this.type === TypeformFormType.INITIAL || TypeformFormType.POST_SIX_MONTHS) {
            //       this.router.navigate(['diagnostic', this.type]);
            //     } else {
            //       this.router.navigateByUrl(this.navigationService.lastPage);
            //     }
            //   });
          } else {
            this.redirectToResults();
          }
        },
        displayAsFullScreenModal: true,
        fullScreen: true,
        hidden: this.hiddenFields,
      };
      
      const isPostLastSession = this.type === TypeformFormType.POST_SESSION && isLastSession;

      const { unmount } = createWidget(isPostLastSession ? this.typeformIdRecord[TypeformFormType.POST_LAST_SESSION] : this.typeformIdRecord[this.type], widgetOptions);

      this.unmount = unmount;
    }
  }

  // Add this helper method
  private redirectToResults(): void {
    this.typeformService.refreshProfile()
      .pipe(
        catchError(error => 
           of(null)
        ),
        finalize(() => {
          this.typeformService.goToResult();
        })
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.unmount();
  }
}
